import { useCallback, useEffect, useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";

const VIEWPORT_INSET = 8;

interface PopoverPosition {
  readonly left: number;
  readonly top: number;
}

export interface UseAnchoredPopoverOptions<TAnchor extends HTMLElement, TPanel extends HTMLElement> {
  readonly open: boolean;
  readonly anchorRef: RefObject<TAnchor | null>;
  readonly panelRef: RefObject<TPanel | null>;
  readonly onRequestClose: () => void;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function viewportDimension(windowDimension: number, documentDimension: number): number {
  return Math.max(windowDimension, documentDimension, VIEWPORT_INSET * 2);
}

/**
 * Keeps a non-modal portal panel attached to its trigger without allowing it
 * to escape the viewport. The caller owns focus restoration so it can make
 * the trigger the single, consistent return point for every close path.
 */
export function useAnchoredPopover<TAnchor extends HTMLElement, TPanel extends HTMLElement>(
  options: UseAnchoredPopoverOptions<TAnchor, TPanel>,
): { readonly style: CSSProperties } {
  const { anchorRef, onRequestClose, open, panelRef } = options;
  const [position, setPosition] = useState<PopoverPosition>({
    left: VIEWPORT_INSET,
    top: VIEWPORT_INSET,
  });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (anchor === null || panel === null) return;

    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = viewportDimension(window.innerWidth, document.documentElement.clientWidth);
    const viewportHeight = viewportDimension(window.innerHeight, document.documentElement.clientHeight);
    const maxLeft = Math.max(VIEWPORT_INSET, viewportWidth - panelRect.width - VIEWPORT_INSET);
    const maxTop = Math.max(VIEWPORT_INSET, viewportHeight - panelRect.height - VIEWPORT_INSET);
    const nextPosition = {
      left: clamp(anchorRect.right - panelRect.width, VIEWPORT_INSET, maxLeft),
      top: clamp(anchorRect.bottom + VIEWPORT_INSET, VIEWPORT_INSET, maxTop),
    };

    setPosition((current) => current.left === nextPosition.left && current.top === nextPosition.top
      ? current
      : nextPosition);
  }, [anchorRef, panelRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      onRequestClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onRequestClose();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [anchorRef, onRequestClose, open, panelRef]);

  return { style: { left: `${position.left}px`, position: "fixed", top: `${position.top}px` } };
}
